import { useAuth } from '@frontegg/react';
import { Card, Typography, Form, Input, Button, message, Spin } from 'antd';
import { useSelector } from 'react-redux';
import { useEffect, useState, useRef, useCallback } from 'react';
import { api } from 'src/services';

const { Title, Paragraph } = Typography;

declare global {
  interface Window {
    DocuSign?: any;
  }
}

const ProviderAgreementPage = () => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const envelopeIdRef = useRef<string | null>(null);
  const [customerName, setCustomerName] = useState<string>('');
  const signingContainerRef = useRef<HTMLDivElement>(null);
  const signingInstanceRef = useRef<any>(null);
  const docusignRef = useRef<any>(null);
  const docusignLoadingPromiseRef = useRef<Promise<any> | null>(null);
  const { settingsOrgValue } = useSelector(
    (state: any) => state?.appointmentState
  );

  const DOCUSIGN_INTEGRATION_KEY = import.meta.env
    .VITE_DOCUSIGN_INTEGRATION_KEY;

  const initializeDocuSignSDK = useCallback(async () => {
    if (!window.DocuSign || !DOCUSIGN_INTEGRATION_KEY || docusignRef.current) {
      return;
    }

    if (!docusignLoadingPromiseRef.current) {
      docusignLoadingPromiseRef.current = window.DocuSign.loadDocuSign(
        DOCUSIGN_INTEGRATION_KEY
      );
    }

    try {
      docusignRef.current = await docusignLoadingPromiseRef.current;
      console.log('[DocuSign] SDK initialized successfully');
    } catch (error) {
      console.error('[DocuSign] Failed to initialize SDK:', error);
      docusignLoadingPromiseRef.current = null;
      throw error;
    }
  }, [DOCUSIGN_INTEGRATION_KEY]);

  const handleSessionEnd = useCallback(() => {
    setIsSubmitting(false);
    envelopeIdRef.current = null;
    setIsLoading(false);
    form.resetFields();

    const orgName = settingsOrgValue?.org_name || '';
    if (orgName) {
      form.setFieldsValue({
        customerName: orgName,
        providerName: user?.name || '',
        providerEmail: user?.email || '',
      });
    }
  }, [form, settingsOrgValue?.org_name, user]);

  const setupSigningEventHandlers = useCallback(
    (signing: any) => {
      signing.on('ready', (event: any) => {
        console.log('[DocuSign] Signing ceremony ready', event);
        setIsLoading(false);
      });

      signing.on('sessionEnd', async (event: any) => {
        console.log('[DocuSign] Session ended', event);

        const currentEnvelopeId = envelopeIdRef.current;

        if (signingInstanceRef.current) {
          try {
            signingInstanceRef.current.unmount();
            signingInstanceRef.current = null;
          } catch (error) {
            console.error('Error unmounting DocuSign instance:', error);
          }
        }

        if (currentEnvelopeId) {
          try {
            await api.post('/v2/ds/envelope/callback', {
              event: event?.sessionEndType,
              envelope_id: currentEnvelopeId,
            });
          } catch (error: any) {
            console.error('[DocuSign] Failed to call callback API:', error);
          }
        }

        if (event.sessionEndType === 'signing_complete') {
          message.success('Document signed successfully!');
        } else {
          message.info('Signing session ended.');
        }

        handleSessionEnd();
      });
    },
    [handleSessionEnd]
  );

  const handleFormSubmit = async (values: any) => {
    try {
      setIsSubmitting(true);
      setIsLoading(true);

      if (!DOCUSIGN_INTEGRATION_KEY) {
        throw new Error('DocuSign integration key is not configured');
      }

      const returnUrl = `${window.location.origin}/docusign-return`;

      const response = await api.post('/v2/ds/envelope/create', {
        provider_email: values.providerEmail,
        provider_name: values.providerName,
        customer_name: values.customerName,
        customer_address: values.customerAddress,
        return_url: returnUrl,
      });

      if (response.data && response.data.url) {
        const signingUrl = response.data.url as string;
        console.log('[DocuSign] Received signing URL:', signingUrl);

        if (response.data.envelope_id) {
          console.log(
            '[DocuSign] Received envelope ID:',
            response.data.envelope_id
          );
          envelopeIdRef.current = response.data.envelope_id;
        }

        if (window.DocuSign && signingContainerRef.current) {
          if (!docusignRef.current) {
            if (!docusignLoadingPromiseRef.current) {
              docusignLoadingPromiseRef.current = window.DocuSign.loadDocuSign(
                DOCUSIGN_INTEGRATION_KEY
              );
            }
            try {
              docusignRef.current = await docusignLoadingPromiseRef.current;
              console.log(
                '[DocuSign] docusignRef.current:',
                docusignRef.current,
                docusignLoadingPromiseRef.current
              );
            } catch (error) {
              docusignLoadingPromiseRef.current = null;
              throw error;
            }
          }

          const signing = docusignRef.current.signing({
            url: signingUrl,
            displayFormat: 'focused',
          });

          console.log('[DocuSign] Signing object created:', signing);

          setupSigningEventHandlers(signing);

          setTimeout(() => {
            if (signingContainerRef.current) {
              console.log(
                '[DocuSign] Mounting to container:',
                signingContainerRef.current.id
              );
              signing.mount(`#${signingContainerRef.current.id}`);
            }
          }, 500);

          signingInstanceRef.current = signing;
        } else {
          throw new Error('DocuSign SDK not loaded');
        }
      } else {
        throw new Error('No signing URL returned');
      }
    } catch (error: any) {
      console.error('Failed to start DocuSign signing', error);
      message.error(
        error?.response?.data?.message || 'Failed to start signing'
      );
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const orgName = settingsOrgValue?.org_name || '';
    setCustomerName(orgName);
    form.setFieldsValue({ customerName: orgName });
  }, [settingsOrgValue?.org_name, form]);

  useEffect(() => {
    initializeDocuSignSDK();
  }, [initializeDocuSignSDK]);

  useEffect(() => {
    return () => {
      if (signingInstanceRef.current) {
        try {
          signingInstanceRef.current.unmount();
        } catch (error) {
          console.error('Error unmounting DocuSign instance:', error);
        }
      }
    };
  }, []);

  return (
    <div className="page-wrapper">
      {isSubmitting ? (
        <div className="flex flex-col bg-[#edf8fe] items-center min-h-screen transition-opacity duration-300 ease-in-out relative">
          {isLoading && (
            <div className="absolute w-[95%] h-[85vh] mt-4 flex items-center justify-center z-10 bg-white bg-opacity-75 rounded-lg">
              <Spin size="large" tip="Loading signing interface..." />
            </div>
          )}
          <div
            id="docusign-container"
            ref={signingContainerRef}
            className={`w-[95%] h-[85vh] mt-4 border border-gray-200 rounded-lg shadow-sm bg-white transition-all duration-300 ease-in-out ${
              isLoading ? 'opacity-0' : 'opacity-100'
            }`}
          />
        </div>
      ) : (
        <div className="bg-[#edf8fe] flex flex-col transition-opacity duration-300 ease-in-out">
          <div className="flex-1 flex flex-col max-w-2xl mx-auto my-8 justify-center">
            <Card className="shadow-md rounded-xl flex flex-col">
              <div className="text-center mb-4">
                <Title level={2} className="!mb-1">
                  Provider Agreements
                </Title>
                <Paragraph type="secondary">
                  Please review the details and sign the service agreement to
                  continue.
                </Paragraph>
              </div>

              <Form
                form={form}
                layout="vertical"
                onFinish={handleFormSubmit}
                initialValues={{
                  providerName: user?.name || '',
                  providerEmail: user?.email || '',
                  customerName: customerName,
                  customerAddress: '',
                }}
                disabled={isLoading || isSubmitting}
              >
                <Form.Item
                  name="customerName"
                  label="Customer Name"
                  rules={[
                    { required: true, message: 'Please enter customer name' },
                  ]}
                >
                  <Input placeholder="Enter customer name" />
                </Form.Item>

                <Form.Item
                  name="customerAddress"
                  label="Customer Address"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter customer address',
                    },
                  ]}
                >
                  <Input placeholder="Enter customer address" />
                </Form.Item>

                <Form.Item
                  name="providerName"
                  label="Provider Name"
                  rules={[
                    { required: true, message: 'Please enter provider name' },
                  ]}
                >
                  <Input placeholder="Enter provider name" />
                </Form.Item>

                <Form.Item
                  name="providerEmail"
                  label="Provider Email"
                  rules={[
                    { required: true, message: 'Please enter provider email' },
                    { type: 'email', message: 'Please enter a valid email' },
                  ]}
                >
                  <Input placeholder="Enter provider email" />
                </Form.Item>

                <div className="mt-4 flex justify-center">
                  <Button
                    className="ih-button-primary h-11 w-full sm:w-auto"
                    onClick={() => form.submit()}
                    loading={isLoading}
                    disabled={isLoading}
                  >
                    Proceed to Signing
                  </Button>
                </div>
              </Form>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderAgreementPage;
